import { motion, TargetAndTransition, Transition } from "motion/react";
import { CSSProperties, FC, PropsWithChildren, useState } from "react";
import useMeasure from "utils/hooks/useMeasure.ts";

interface ExpandCollapseProps {
  expanded: boolean;
  style?: CSSProperties;
  onAnimationComplete?: () => void;
  horizontal?: boolean;
  hideOnCollapse?: boolean;
  overflowVisible?: boolean;
  layout?: boolean;
  transitionProps?: Transition;
  animate?: TargetAndTransition;
  animateAuto?: boolean;
}

const ExpandCollapse: FC<PropsWithChildren<ExpandCollapseProps>> = ({
  children,
  expanded,
  style,
  onAnimationComplete,
  horizontal,
  hideOnCollapse = true,
  overflowVisible,
  layout,
  transitionProps,
  animate,
  animateAuto = true,
}) => {
  const [animating, setAnimating] = useState(false);
  const [localExpanded, setLocalExpanded] = useState(expanded);
  const [measureRef, { height, width }] = useMeasure<HTMLDivElement>();

  const expandedSize = animateAuto ? "auto" : horizontal ? width : height;
  const size = expanded ? expandedSize : 0;

  const animateProp: TargetAndTransition = {
    opacity: !hideOnCollapse || expanded ? 1 : 0,
    ...animate,
  };
  if (!expanded || width || height) {
    if (horizontal) {
      animateProp.width = size;
    } else {
      animateProp.height = size;
    }
  }

  const isAnimationComplete = (animationDefinition: CSSProperties) => {
    return animationDefinition.opacity === (expanded ? 1 : 0);
  };

  const isAnimating = animating || expanded !== localExpanded;

  return (
    <motion.div
      initial={false}
      layout={layout}
      animate={animateProp}
      style={{
        overflow:
          (expanded && !isAnimating) || overflowVisible ? "visible" : "hidden",
        pointerEvents: expanded && !isAnimating ? "inherit" : "none",
        display: !expanded && !isAnimating && hideOnCollapse ? "none" : "block",
        willChange: horizontal ? "width" : "height",
      }}
      transition={{ type: "tween", ...transitionProps }}
      onAnimationStart={() => setAnimating(true)}
      onAnimationComplete={(animationDefinition) => {
        if (isAnimationComplete(animationDefinition as CSSProperties)) {
          onAnimationComplete?.();
          setAnimating(false);
          setLocalExpanded(expanded);
        }
      }}
    >
      <div ref={measureRef}>
        <div style={style}>{children}</div>
      </div>
    </motion.div>
  );
};

export default ExpandCollapse;
